/**
 * Breeding: heat, synchronization, AI, bull exposure, pregnancy checks, calving and newborns,
 * losses, fostering, embryo transfer and breeding decisions. Every fact is an event; the `breeding`
 * facet turns them into each female's status and calculated due date. Semen and embryo stock belong
 * to genetics; services here reference its lots and use one straw or embryo through a stock effect.
 */
import { addDays, defineEvent, defineFacet, defineModule, f, msg, type FollowUp, type Issue, type SubjectRef } from "../../contract";
import { DATE_MESSAGES } from "./shared/dates";
import { ISSUES, ISSUE_MESSAGES } from "./shared/issues";
import {
  AGE_UNIT_LABELS, AI_KINDS, AI_RESULTS, BASIS_LABELS, BSE_RESULTS, CALF_OUTCOME, CALF_SEX, CHECK_METHODS, CHECK_RESULTS, DECISIONS, EASE, EMBRYO_STATES, EXPOSURE_ACTIONS,
  FETAL_SEX, FOSTER_METHODS, FOSTER_REASONS, FOSTER_RESULTS, HEAT_METHODS, HEAT_SCALES, LOSS_KINDS, OBSERVED, PRESENTATION, REASONS, SIGNS, SIRE_BASIS, STAGES,
  SHORT_STATUS, STATUS_LABELS, TRANSFER_RESULTS, USES, WEIGHT_BASIS,
} from "./shared/labels";
import { PROTOCOL_LABELS, PROTOCOL_MESSAGES, SYNC_ACTIONS } from "./shared/protocols";
import { FACET_VERSION, FACT_LABELS, breedingBadges, breedingFacts, gestationFrom, reduceBreeding } from "./shared/state";
import type {
  AiData, BreedingState, CalvingData, EligibilityData, ExposureData, FosterData, HeatData, LossData, PregCheckData, SyncStepData, TransferData,
} from "./shared/types";

export type * from "./shared/types";

const animals = (subjects: SubjectRef[], role: string) => subjects.filter((s) => s.kind === "animal" && s.role === role);
const one = (subjects: SubjectRef[], role: string) => animals(subjects, role)[0];
const notes = () => f.longText("Notes", { level: "more", agent: "Only extra details they said, in their own words and language." });
const AGE_UNITS = ["d", "wk", "mo"] as const;

// ---------------------------------------------------------------- heat and synchronization

const heat = defineEvent<HeatData>({
  type: "breeding.heat",
  label: "Heat seen",
  description: "A cow or heifer showed signs of heat.",
  icon: "flame",
  tone: "repro",
  subjects: [{ role: "animal", kind: "animal", label: "Cows and heifers", many: true, required: true }],
  fields: {
    signs: f.multiEnum("Signs", SIGNS),
    method: f.enum("How it was spotted", HEAT_METHODS, { level: "more" }),
    score: f.number("Heat score", { level: "more", min: 0, max: 100, help: "Keep the score on the scale you use." }),
    scale: f.enum("Score scale", HEAT_SCALES, { level: "more" }),
    observer_id: f.ref("Seen by", "member", { level: "more" }),
    remind_ai: f.bool("Remind me to breed her", { level: "more", agent: "true only when they ask to be reminded or say she needs breeding" }),
    notes: notes(),
  },
  summary: (e, x) => (e.data.signs?.length ? x.list(e.data.signs.map((s) => x.t(SIGNS[s as keyof typeof SIGNS] ?? s))) : x.t("Heat seen")),
  agent: {
    when: "Someone saw a cow or heifer in heat (standing, riding, mucus, rubbed heat patch). An observation only, not a breeding.",
    avoid: "Not for AI or a bull breeding her (breeding.ai, breeding.exposure).",
    examples: ["541 standing at six this morning", "the heat patch on 2507 is rubbed off", "118 riding the others"],
  },
  validate: (d) => (d.score !== undefined && d.scale === "1_5" && d.score > 5 ? [{ path: "data.score", message: ISSUES.scoreScale }] : []),
  effects: {
    followUps: (d, on, subjects) => (d.remind_ai && on ? animals(subjects, "animal").map((a): FollowUp => ({
      key: `breed:${a.id}`, title: msg("Breed her: heat seen"), due_at: on, subject: { kind: "animal", id: a.id },
    })) : []),
  },
});

const syncStep = defineEvent<SyncStepData>({
  type: "breeding.sync_step",
  label: "Synchronization step",
  description: "A step of a heat synchronization protocol was done: a CIDR in or out, a shot, MGA feeding.",
  icon: "timer",
  tone: "repro",
  subjects: [
    { role: "animal", kind: "animal", label: "Cows and heifers", many: true, required: true },
    { role: "group", kind: "group", label: "Group" },
  ],
  fields: {
    actions: f.multiEnum("What was done", SYNC_ACTIONS, { required: true }),
    protocol: f.enum("Protocol", PROTOCOL_LABELS),
    protocol_day: f.int("Protocol day", { min: 0, max: 60, help: "Day 0 is the first day of the protocol." }),
    product_note: f.text("Products", { level: "more", help: "Record shots as health treatments for withdrawal and stock. Note them here only for reference." }),
    notes: notes(),
  },
  summary: (e, x) => {
    const what = x.list(e.data.actions.map((a) => x.t(SYNC_ACTIONS[a as keyof typeof SYNC_ACTIONS] ?? a)));
    const head = x.count(animals(e.subjects, "animal").length, "{{count}} head", "{{count}} head");
    return e.data.protocol_day !== undefined ? x.t("Day {{day}}: {{what}}, {{head}}", { day: e.data.protocol_day, what, head }) : x.t("{{what}}, {{head}}", { what, head });
  },
  agent: {
    when: "A synchronization step was done on cows or heifers: CIDRs in or out, GnRH or PG shots, MGA started or stopped. List every animal.",
    avoid: "The AI itself is breeding.ai. The drug and dose given go in a health treatment if health records it.",
    examples: ["pulled CIDRs and gave Lutalyse to the heifers", "CIDRs in on the spring cows, day 0 of 7-day CO-Synch"],
  },
  validate: (d) => (!d.actions?.length ? [{ path: "data.actions", message: ISSUES.syncActions }] : []),
});

// ---------------------------------------------------------------- services

const ai = defineEvent<AiData>({
  type: "breeding.ai",
  label: "AI",
  description: "A cow or heifer was artificially inseminated.",
  icon: "syringe",
  tone: "repro",
  subjects: [{ role: "animal", kind: "animal", label: "Cows and heifers", many: true, required: true }],
  fields: {
    sire_id: f.ref("Sire", "animal", {
      filter: { sex: "male", status: "all" }, help: "Outside bulls can be added as reference animals.",
      agent: "The AI sire. Look him up with find_animals (status all: outside sires are reference animals). If he isn't in the records, use sire_name.",
    }),
    semen_lot_id: f.ref("Semen", "stock_lot", { filter: { kind: "semen" }, agent: "The semen lot used, from find_records stock_item. Only when they name the lot, canister or batch; never guess a lot." }),
    straws: f.int("Straws per cow", { level: "more", min: 1, max: 4, help: "Leave blank for one straw." }),
    kind: f.enum("Bred on", AI_KINDS, { level: "more" }),
    technician_id: f.ref("Technician", "member", { level: "more" }),
    technician_contact_id: f.ref("Outside technician", "contact", { level: "more" }),
    sire_name: f.text("Sire name", { level: "more", help: "When the bull isn't in your records." }),
    result: f.enum("Result", AI_RESULTS, { level: "more", agent: "failed only when they say the insemination couldn't be done" }),
    notes: notes(),
  },
  summary: (e, x) => {
    const sire = e.data.sire_id ? x.label(e.data.sire_id) : e.data.sire_name;
    const n = animals(e.subjects, "animal").length;
    const base = sire ? x.t("AI to {{sire}}", { sire }) : x.t("AI, sire not recorded");
    const parts = [base];
    if (n > 1) parts.push(x.count(n, "{{count}} head", "{{count}} head"));
    if (e.data.straws && e.data.straws > 1) parts.push(x.count(e.data.straws, "{{count}} straw each", "{{count}} straws each"));
    if (e.data.result === "failed") parts.push(x.t("couldn't finish"));
    return parts.join(" · ");
  },
  review: "auto",
  agent: {
    when: "Cows or heifers were artificially inseminated (AI'd, bred AI, timed AI). One event can list several animals bred to the same sire.",
    avoid: "Not for a bull turned in (breeding.exposure) or a heat seen (breeding.heat). Not for a planned AI: that's a task or an AI and sync round.",
    examples: ["AI'd 541 at five, Ranger, red tank canister three, Sam did it", "bred the 12 heifers to Inspiration this morning"],
  },
  effects: {
    stock: (d, subjects) => (d.semen_lot_id ? [{ lot_id: d.semen_lot_id, delta: -(d.straws ?? 1) * Math.max(1, animals(subjects, "animal").length), unit: "straw", reason: "use" as const }] : []),
    followUps: (d, on, subjects) => {
      if (!on || d.result === "failed") return [];
      const list = animals(subjects, "animal");
      return [{
        key: "repeat-heat", due_at: addDays(on, 18),
        title: list.length === 1 ? msg("Watch for a repeat heat") : msg("Watch {{count}} head for repeat heats", { count: list.length }),
        ...(list.length === 1 ? { subject: { kind: "animal" as const, id: list[0].id } } : {}),
      }];
    },
  },
});

const exposure = defineEvent<ExposureData>({
  type: "breeding.exposure",
  label: "Bull in or out",
  description: "A bull was turned in with cows or pulled out: the natural-service exposure window.",
  icon: "fence",
  tone: "repro",
  subjects: [
    { role: "bull", kind: "animal", label: "Bulls", many: true, required: true },
    { role: "female", kind: "animal", label: "Cows and heifers", many: true, help: "Everyone the bulls were with. Picking the group fills this in." },
    { role: "group", kind: "group", label: "Group" },
    { role: "place", kind: "place", label: "Pasture" },
  ],
  fields: {
    action: f.enum("Bull in or out", EXPOSURE_ACTIONS, { required: true }),
    notes: notes(),
  },
  summary: (e, x) => {
    const bulls = animals(e.subjects, "bull").map((s) => s.label ?? x.label(s.id));
    const cows = x.count(animals(e.subjects, "female").length, "{{count}} cow", "{{count}} cows");
    const who = bulls.length ? x.list(bulls) : x.t("Bull");
    return e.data.action === "in" ? x.t("{{bulls}} in with {{cows}}", { bulls: who, cows }) : x.t("{{bulls}} out from {{cows}}", { bulls: who, cows });
  },
  agent: {
    when: "Bulls were turned in with cows or heifers (bull in), or pulled out (bull out). List every bull with role bull and every female he was with with role female (expand the group; for bull out use breeding_exposed_to_bull). Add the group and pasture.",
    avoid: "An ordinary move of a bull with no cows is a land move, not this. A bull merely in the same pasture without anyone saying he's breeding is not an exposure.",
    examples: ["turned Ranger and 118 in with the north cows today", "pulled B12 yesterday", "bulls out of the creek trap"],
  },
  validate: (d, subjects) => (!animals(subjects, "female").length && !subjects.some((s) => s.role === "group") ? [{ path: "subjects.female", message: ISSUES.exposureFemales }] : []),
});

const transfer = defineEvent<TransferData>({
  type: "breeding.embryo_transfer",
  label: "Embryo transfer",
  description: "An embryo was put into a recipient cow. The donor stays the genetic dam.",
  icon: "snowflake",
  tone: "repro",
  subjects: [
    { role: "recipient", kind: "animal", label: "Recipient", required: true },
    { role: "donor", kind: "animal", label: "Donor (genetic dam)" },
  ],
  fields: {
    embryo_lot_id: f.ref("Embryo", "stock_lot", { filter: { kind: "embryo" }, agent: "The embryo lot from find_records stock_item (embryos). Only when said; never guess." }),
    sire_id: f.ref("Sire", "animal", { filter: { sex: "male", status: "all" } }),
    embryo_age_days: f.int("Embryo age", { level: "more", min: 5, max: 9, suffix: "days", help: "Leave blank if not known. Due dates then assume 7 days and say so." }),
    embryo_state: f.enum("Embryo was", EMBRYO_STATES, { level: "more" }),
    grade: f.text("Grade", { level: "more" }),
    technician_contact_id: f.ref("Technician or vet", "contact", { level: "more" }),
    result: f.enum("Result", TRANSFER_RESULTS, { level: "more" }),
    notes: notes(),
  },
  summary: (e, x) => {
    const donor = one(e.subjects, "donor");
    const sire = e.data.sire_id ? x.label(e.data.sire_id) : null;
    if (e.data.result === "not_transferred") return x.t("Embryo thawed, not transferred");
    if (donor && sire) return x.t("Embryo from {{donor}} × {{sire}}", { donor: donor.label ?? x.label(donor.id), sire });
    if (donor) return x.t("Embryo from {{donor}}", { donor: donor.label ?? x.label(donor.id) });
    return x.t("Embryo transferred");
  },
  review: "always",
  agent: {
    when: "An embryo was transferred into a recipient cow (ET). The recipient carries the calf; the donor is the genetic dam.",
    avoid: "Not for flushing a donor or storing embryos (genetics). Not for a planned transfer.",
    examples: ["transferred donor 50 by Ranger embryo three into 818 this afternoon, Dr Lee"],
  },
  validate: (_d, subjects) => {
    const r = one(subjects, "recipient");
    const d = one(subjects, "donor");
    return r && d && r.id === d.id ? [{ path: "subjects.donor", message: ISSUES.donorIsRecipient }] : [];
  },
  effects: {
    stock: (d) => (d.embryo_lot_id ? [{ lot_id: d.embryo_lot_id, delta: -1, unit: "embryo", reason: d.result === "not_transferred" ? "waste" as const : "use" as const }] : []),
    followUps: (d, on, subjects) => {
      const r = one(subjects, "recipient");
      return on && r && d.result !== "not_transferred" ? [{ key: "preg-check", title: msg("Preg check the recipient"), due_at: addDays(on, 30), subject: { kind: "animal", id: r.id } }] : [];
    },
  },
});

// ---------------------------------------------------------------- pregnancy

const pregCheck = defineEvent<PregCheckData>({
  type: "breeding.preg_check",
  label: "Preg check",
  description: "A pregnancy check: pregnant, open or recheck, with fetal age when known.",
  icon: "vet",
  tone: "repro",
  subjects: [{ role: "animal", kind: "animal", label: "Cow or heifer", required: true }],
  fields: {
    result: f.enum("Result", CHECK_RESULTS, { required: true, agent: "pregnant (bred, settled, safe, P), open (empty, O), recheck (not sure yet, R)" }),
    fetal_age: f.quantity("Fetal age", "duration", {
      units: AGE_UNITS, help: "How far along she is. Keep the unit the vet used.",
      agent: "Days bred or fetal age as said, with its unit d, wk or mo: '95 days' is {value:95,unit:'d'}, '3 months' is {value:3,unit:'mo'}. A bare number with no unit: ask unless the ranch clearly works in days.",
    }),
    method: f.enum("Method", CHECK_METHODS),
    twins: f.bool("Twins", { level: "more", agent: "true only when the examiner said twins" }),
    recheck_on: f.date("Recheck on", { level: "more", agent: "Only for a recheck result when a date or 'in N weeks' was said" }),
    stage: f.enum("Stage", STAGES, { level: "more", agent: "Only when they describe her as short bred, middle or heavy bred without a number" }),
    fetal_sex: f.enum("Fetal sex", FETAL_SEX, { level: "more" }),
    examiner_id: f.ref("Vet or examiner", "contact", { level: "more" }),
    notes: notes(),
  },
  summary: (e, x) => {
    const d = e.data;
    const parts = [x.t(CHECK_RESULTS[d.result] ?? d.result)];
    if (d.result === "pregnant" && d.fetal_age) parts.push(x.quantity(d.fetal_age));
    if (d.twins) parts.push(x.t("twins"));
    if (d.result === "recheck" && d.recheck_on) parts.push(x.t("on {{date}}", { date: x.date(d.recheck_on) }));
    if (d.method) parts.push(x.t(CHECK_METHODS[d.method] ?? d.method).toLowerCase());
    return parts.join(" · ");
  },
  agent: {
    when: "A cow or heifer was checked for pregnancy (palpated, ultrasounded, blood test): pregnant, open, or recheck. One event per animal; a chute list of results is several events.",
    avoid: "A due date someone mentions is not a check. A loss after being pregnant is breeding.loss.",
    examples: ["212 bred 90 days, heifer; 214 open; 215 recheck in three weeks", "Doc palpated 42, she's safe, about 4 months", "118 is open"],
  },
  validate: (d) => {
    const out: Issue[] = [];
    if (d.fetal_age && !AGE_UNITS.includes(d.fetal_age.unit as (typeof AGE_UNITS)[number])) out.push({ path: "data.fetal_age", message: ISSUES.ageUnit });
    if (d.fetal_age && d.result !== "pregnant") out.push({ path: "data.fetal_age", message: ISSUES.ageNotPregnant });
    if (d.recheck_on && d.result !== "recheck") out.push({ path: "data.recheck_on", message: ISSUES.recheckNotPending });
    return out;
  },
  effects: {
    followUps: (d, _on, subjects) => {
      const a = one(subjects, "animal");
      return d.result === "recheck" && d.recheck_on && a ? [{ key: "recheck", title: msg("Recheck for pregnancy"), due_at: d.recheck_on, subject: { kind: "animal", id: a.id } }] : [];
    },
  },
});

const loss = defineEvent<LossData>({
  type: "breeding.loss",
  label: "Pregnancy loss",
  description: "A cow aborted or lost a pregnancy before calving.",
  icon: "heartCrack",
  tone: "repro",
  subjects: [{ role: "animal", kind: "animal", label: "Cow or heifer", required: true }],
  fields: {
    kind: f.enum("What happened", LOSS_KINDS, { required: true }),
    fetal_age: f.quantity("How far along", "duration", { level: "more", units: AGE_UNITS }),
    cause: f.text("Cause", { level: "more", help: "Only what a vet or lab reported." }),
    notes: notes(),
  },
  summary: (e, x) => [x.t(LOSS_KINDS[e.data.kind] ?? e.data.kind), e.data.fetal_age ? x.quantity(e.data.fetal_age) : null, e.data.cause].filter(Boolean).join(" · "),
  review: "always",
  agent: {
    when: "A pregnant cow aborted, or was found open after having been pregnant (pregnancy lost before calving).",
    avoid: "A calf born dead at term is a calving with outcome born_dead. A calf that died after birth is a death (herd).",
    examples: ["found an aborted calf behind 150", "142 was bred, now she's open, lost it"],
  },
  validate: (d) => (d.fetal_age && !AGE_UNITS.includes(d.fetal_age.unit as (typeof AGE_UNITS)[number]) ? [{ path: "data.fetal_age", message: ISSUES.ageUnit }] : []),
});

// ---------------------------------------------------------------- calving

const calving = defineEvent<CalvingData>({
  type: "breeding.calving",
  label: "Calving",
  description: "A cow or heifer gave birth: each calf, how it went and who helped.",
  icon: "calf",
  tone: "repro",
  subjects: [
    { role: "dam", kind: "animal", label: "Cow or heifer", required: true },
    { role: "calf", kind: "animal", label: "Calves", many: true, help: "Calves that already have a record. New live calves get one when this is saved." },
  ],
  fields: {
    calves: f.list("Calves", f.object("Calf", {
      sex: f.enum("Sex", CALF_SEX),
      tag: f.tag("Tag"),
      outcome: f.enum("At birth", CALF_OUTCOME, { required: true, agent: "alive unless they say born dead or stillborn" }),
      calf_id: f.ref("Calf record", "animal", { level: "more", agent: "The calf's animal id: new:<temp_id> from a create item, or an existing calf. Also list it in subjects with role calf." }),
      weight: f.quantity("Birth weight", "weight", { level: "more", agent: "Only when a weight was said" }),
      weight_basis: f.enum("Weight was", WEIGHT_BASIS, { level: "more", agent: "measured when weighed on a scale or tape; estimated when guessed" }),
      color: f.text("Color", { level: "more" }),
    }), { max: 4, itemLabel: "Calf" }),
    ease: f.enum("Calving ease", EASE, { agent: "no_help when they say no help or unassisted; only what was said" }),
    observed: f.enum("Seen or found", OBSERVED, { level: "more", agent: "found when they found her with the calf instead of seeing the birth" }),
    presentation: f.enum("Presentation", PRESENTATION, { level: "more" }),
    assisted_by: f.ref("Helped by", "member", { level: "more" }),
    vet_id: f.ref("Vet", "contact", { level: "more" }),
    sire_id: f.ref("Sire", "animal", { level: "more", filter: { sex: "male", status: "all" }, agent: "Only from breeding_status: the likely sire of this pregnancy. Never guess." }),
    sire_basis: f.enum("Sire is", SIRE_BASIS, { level: "more", agent: "confirmed only for AI matched by date, ET or DNA; likely when breeding records point to one bull; unknown otherwise" }),
    notes: notes(),
  },
  summary: (e, x) => {
    const d = e.data;
    const calves = d.calves ?? [];
    const calf = (c: (typeof calves)[number]) => [
      c.sex === "female" ? x.t("heifer calf") : c.sex === "male" ? x.t("bull calf") : x.t("calf"),
      c.tag ?? (c.calf_id && !c.calf_id.startsWith("new:") ? x.label(c.calf_id) : ""),
      c.outcome === "born_dead" ? x.t("born dead") : "",
    ].filter(Boolean).join(" ");
    const head = calves.length ? `${calves.length > 1 ? `${x.t("Twins")}: ` : ""}${calves.map(calf).join(", ")}` : x.t("Calved");
    return [head, d.ease ? x.t(EASE[d.ease] ?? d.ease).toLowerCase() : null].filter(Boolean).join(" · ");
  },
  review: "always",
  agent: {
    when: "A cow or heifer gave birth, including twins and calves born dead. Call breeding_status for the dam first. For each live calf, add a create item (entity animal, temp_id new:calf1, data: tag, sex, birth_date = calving date, dam_id = the dam, sire_id only when breeding_status gives a likely sire, origin born) and put it on this event both as a subject with role calf and in data.calves[].calf_id, copying the tag into calves[].tag. A born-dead calf goes in data.calves with outcome born_dead and normally gets no animal record.",
    avoid: "If breeding_status shows a calving for her in the last few days, it is the same birth: do not propose a second calving; say so in a note. A calf that died days after birth is a death (herd), not born_dead. A due date is not a calving.",
    examples: ["42 calved this morning, heifer, tagged 42P, no help", "813 calved overnight, black bull, 78 pounds, tag 640, pulled him", "613 had twins, one dead, the live heifer is 77"],
  },
  validate: (d) => {
    const out: Issue[] = [];
    (d.calves ?? []).forEach((c, i) => { if (c.weight_basis && !c.weight) out.push({ path: `data.calves.${i}.weight`, message: ISSUES.weightBasis }); });
    if ((d.sire_basis === "confirmed" || d.sire_basis === "likely") && !d.sire_id) out.push({ path: "data.sire_id", message: ISSUES.sireMissing });
    return out;
  },
  effects: {
    followUps: (d, on, subjects) => {
      if (!on) return [];
      const dam = one(subjects, "dam");
      return (d.calves ?? []).flatMap((c, i): FollowUp[] => (c.outcome === "alive" && !c.tag ? [{
        key: `tag:${i}`, title: msg("Tag the new calf"), due_at: on,
        subject: c.calf_id && !c.calf_id.startsWith("new:") ? { kind: "animal", id: c.calf_id } : dam ? { kind: "animal", id: dam.id } : undefined,
      }] : []));
    },
  },
});

const foster = defineEvent<FosterData>({
  type: "breeding.foster",
  label: "Graft or foster",
  description: "A calf was put on a nurse cow. Its own dam stays its dam in the pedigree.",
  icon: "heartHandshake",
  tone: "repro",
  subjects: [
    { role: "calf", kind: "animal", label: "Calf", required: true },
    { role: "nurse", kind: "animal", label: "Nurse cow", required: true },
    { role: "dam", kind: "animal", label: "Calf's own dam", help: "Stays the calf's dam in the pedigree." },
  ],
  fields: {
    reason: f.enum("Why", FOSTER_REASONS),
    method: f.enum("How", FOSTER_METHODS, { level: "more" }),
    result: f.enum("Did she take it", FOSTER_RESULTS, { level: "more" }),
    notes: notes(),
  },
  summary: (e, x) => {
    const calf = one(e.subjects, "calf");
    const nurse = one(e.subjects, "nurse");
    const base = x.t("{{calf}} on {{nurse}}", { calf: calf ? calf.label ?? x.label(calf.id) : x.t("calf"), nurse: nurse ? nurse.label ?? x.label(nurse.id) : "" });
    return [base, e.data.reason ? x.t(FOSTER_REASONS[e.data.reason] ?? e.data.reason).toLowerCase() : null, e.data.result === "failed" ? x.t("didn't take") : null].filter(Boolean).join(" · ");
  },
  agent: {
    when: "A calf was grafted or put on a nurse cow that isn't its dam (twin, orphan, dam with no milk, nurse cow that lost her own calf). Add the calf's own dam with role dam when known.",
    avoid: "Never change the calf's dam for this; pedigree stays with the genetic dam.",
    examples: ["put 77 on 620 this afternoon", "grafted the twin onto 137, she lost hers"],
  },
  validate: (_d, subjects) => {
    const calf = one(subjects, "calf");
    const nurse = one(subjects, "nurse");
    const dam = one(subjects, "dam");
    if (calf && nurse && calf.id === nurse.id) return [{ path: "subjects.nurse", message: ISSUES.nurseIsCalf }];
    if (dam && nurse && dam.id === nurse.id) return [{ path: "subjects.nurse", message: ISSUES.nurseIsDam }];
    return [];
  },
  effects: {
    followUps: (d, on, subjects) => {
      const calf = one(subjects, "calf");
      return d.result === "trying" && on && calf ? [{ key: "graft-check", title: msg("Check the graft"), due_at: addDays(on, 2), subject: { kind: "animal", id: calf.id } }] : [];
    },
  },
});

// ---------------------------------------------------------------- decisions

const eligibility = defineEvent<EligibilityData>({
  type: "breeding.eligibility",
  label: "Breeding decision",
  description: "Keep, cull, sell open or wait: the ranch's decision about breeding an animal, with the exam behind it.",
  icon: "clipboardCheck",
  tone: "repro",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", many: true, required: true }],
  fields: {
    decision: f.enum("Decision", DECISIONS, { required: true }),
    reasons: f.multiEnum("Why", REASONS),
    use: f.enum("For", USES, { level: "more" }),
    bse_result: f.enum("Breeding soundness exam", BSE_RESULTS, { level: "more", help: "The vet's finding as reported. The exam itself is a health record." }),
    bse_on: f.date("Exam date", { level: "more" }),
    examiner_id: f.ref("Examiner", "contact", { level: "more" }),
    review_on: f.date("Look again on", { level: "more" }),
    notes: f.longText("Notes", { level: "more", help: "Required when keeping an animal despite an unsatisfactory exam." }),
  },
  summary: (e, x) => [x.t(DECISIONS[e.data.decision] ?? e.data.decision), e.data.reasons?.length ? x.list(e.data.reasons.map((r) => x.t(REASONS[r as keyof typeof REASONS] ?? r)).map((s) => s.toLowerCase())) : null].filter(Boolean).join(" · "),
  review: "always",
  permission: "manage",
  agent: {
    when: "The owner or manager decided whether to keep breeding an animal: keep, cull, sell open, don't breed, or decide later. Include the reason and any breeding soundness exam result reported.",
    avoid: "The exam itself (motility, scrotal) belongs to health. Selling or shipping the animal is a herd departure.",
    examples: ["sell the open heifers", "keep 142 one more year, she's raised good calves", "Dr. Ramirez deferred B31, look again November 20"],
  },
  validate: (d) => (d.decision === "keep" && d.bse_result === "unsatisfactory" && !d.notes?.trim() ? [{ path: "data.notes", message: ISSUES.overrideReason }] : []),
  effects: {
    followUps: (d, _on, subjects) => {
      const list = animals(subjects, "animal");
      return d.review_on ? [{ key: "review", title: msg("Look again at the breeding decision"), due_at: d.review_on, ...(list.length === 1 ? { subject: { kind: "animal" as const, id: list[0].id } } : {}) }] : [];
    },
  },
});

// ---------------------------------------------------------------- facet

export const BREEDING_EVENT_TYPES = [
  "breeding.heat", "breeding.sync_step", "breeding.ai", "breeding.exposure", "breeding.embryo_transfer", "breeding.preg_check",
  "breeding.loss", "breeding.calving", "breeding.foster", "breeding.eligibility",
];

export const breedingFacet = defineFacet<BreedingState>({
  id: "breeding",
  subject: "animal",
  version: FACET_VERSION,
  eventTypes: BREEDING_EVENT_TYPES,
  reduce: (events, ctx) => reduceBreeding(events, { gestation: gestationFrom(ctx.settings) }),
  badges: (s, { today }) => breedingBadges(s, today),
  facts: (s, { today }) => breedingFacts(s, today),
  filters: {
    status: { label: "Breeding status", type: "enum", options: STATUS_LABELS },
    due_date: { label: "Calving due date", type: "date" },
    has_calved: { label: "Has calved", type: "boolean" },
  },
});

// ---------------------------------------------------------------- module

export default defineModule({
  id: "breeding",
  label: "Breeding",
  description: "Exposure, heat, AI, pregnancy, calving and embryo transfer.",
  events: [heat, syncStep, ai, exposure, transfer, pregCheck, loss, calving, foster, eligibility],
  facets: [breedingFacet],
  jobs: [
    {
      type: "breeding.season", label: "Breeding season", description: "Bulls, cows and dates for one breeding season, from turnout to preg check.",
      icon: "heart", tone: "repro", workspace: "breeding", subjectKind: "animal",
      fields: { bulls_in_on: f.date("Bulls in"), bulls_out_on: f.date("Bulls out"), bull_ids: f.refs("Bulls", "animal"), plan: f.multiEnum("Plan", { ai: "AI first", natural: "Natural service", et: "Embryo transfer" }) },
      agent: { when: "Planning a breeding season for a group: which bulls, which cows, turn-in and pull dates. Put bull ids in data.bull_ids and the dates in data.bulls_in_on and data.bulls_out_on." },
    },
    {
      type: "breeding.preg_check_round", label: "Preg check", description: "Check a set of cows for pregnancy, one tag at a time.",
      icon: "vet", tone: "repro", workspace: "breeding", subjectKind: "animal", itemEvent: "breeding.preg_check",
      fields: { method: f.enum("Method", CHECK_METHODS), examiner_id: f.ref("Vet or examiner", "contact") },
      agent: { when: "They want to preg check a group of cows later ('preg check the spring cows Tuesday'). Subjects are every cow to check." },
    },
    {
      type: "breeding.sync_round", label: "AI and sync round", description: "A synchronization protocol and AI for a set of cows, with each step on the calendar.",
      icon: "timer", tone: "repro", workspace: "breeding", subjectKind: "animal", itemEvent: "breeding.ai",
      fields: { protocol: f.enum("Protocol", PROTOCOL_LABELS), day0: f.date("Day 0"), sire_id: f.ref("Sire", "animal"), semen_lot_id: f.ref("Semen", "stock_lot"), technician_id: f.ref("Technician", "member") },
      agent: { when: "They're starting a synchronization protocol or timed AI for a group ('CIDRs in on the heifers Monday, 7-day CO-Synch'). Put the protocol and day 0 in data." },
    },
    {
      type: "breeding.calving_season", label: "Calving season", description: "Follow the cows due to calve, in order of due date.",
      icon: "calf", tone: "repro", workspace: "calving", subjectKind: "animal", itemEvent: "breeding.calving",
      fields: { starts_on: f.date("Starts"), ends_on: f.date("Ends") },
      agent: { when: "They want to follow a calving season for a group of cows." },
    },
  ],
  workspaces: [
    {
      id: "breeding", label: "Manage breeding", description: "Breeding seasons, bulls in and out, AI and sync, preg checks and keep-or-cull decisions.",
      icon: "heart", group: "breed", presets: ["seedstock", "commercial", "dairy"],
      keywords: ["breeding", "bull", "bulls in", "bulls out", "turnout", "AI", "artificial insemination", "inseminate", "synchronization", "sync", "CIDR", "heat", "preg check", "pregnancy", "palpate", "palpation", "ultrasound", "open cows", "embryo transfer", "ET", "recipient", "cull", "BSE", "exposure", "season"],
      keywordsEs: ["empadre", "monta", "toro", "toros", "inseminar", "inseminación", "IA", "sincronización", "celo", "palpar", "palpación", "preñez", "preñada", "vacía", "vacías", "ultrasonido", "transferencia de embriones", "receptora", "descarte", "temporada"],
    },
    {
      id: "calving", label: "Record calving", description: "Who's due, calves born this season, twins, losses and nurse cows.",
      icon: "calf", group: "breed", presets: ["basic", "seedstock", "commercial", "dairy"],
      keywords: ["calving", "calved", "calf", "calves", "birth", "newborn", "due", "twins", "graft", "foster", "nurse cow", "stillborn", "born dead", "calving ease", "pull"],
      keywordsEs: ["parto", "partos", "parió", "parir", "becerro", "becerra", "becerros", "nacimiento", "pariciones", "cuates", "gemelos", "nodriza", "adoptar", "nació muerto", "jalar"],
    },
  ],
  settings: {
    gestation_days: f.int("Gestation length", { min: 260, max: 300, suffix: "days", help: "Used to work out due dates from breeding dates and fetal age. Beef cows average about 283 days." }),
  },
  messages: [
    ...DATE_MESSAGES, ...ISSUE_MESSAGES, ...PROTOCOL_MESSAGES,
    ...Object.values(STATUS_LABELS), ...Object.values(BASIS_LABELS), ...Object.values(DECISIONS), ...Object.values(CHECK_RESULTS), ...Object.values(EASE),
    ...Object.values(CALF_SEX), ...Object.values(CALF_OUTCOME), ...Object.values(SIRE_BASIS), ...Object.values(REASONS), ...Object.values(FOSTER_REASONS),
    ...Object.values(LOSS_KINDS), ...Object.values(AI_KINDS), ...Object.values(SIGNS), ...Object.values(USES),
    ...Object.values(SHORT_STATUS), ...Object.values(AGE_UNIT_LABELS), ...FACT_LABELS,
    // Animal page section and action labels (web.tsx), translated by the shell.
    "Reproduction", "Record calving", "Preg check", "Record AI", "Heat seen", "Bull in", "Bull out", "Breeding decision", "Put a calf on her",
  ],
});
